import { useCallback, useEffect, useRef, useTransition, type TransitionStartFunction } from 'react';

/**
 * The refresh coordinator: the one place that decides WHEN the current route
 * is refreshed, and through the runner that `RefreshHost` registers
 * (src/components/refresh-host.tsx) the only caller of `router.refresh()`.
 *
 * **One refresh per write (2026-09-28).** A write is a server action, and every
 * mutating action revalidates `/` (`src/app/actions.ts`), so the action's own
 * response already carries the refreshed tree: the router applies it as a
 * navigation and fetches the parts the response did not render (the board
 * under an item peek). Before this date every write also produced further
 * refreshes: the component's own `router.refresh()` after the action, the item
 * page's record bridge after the board push, and the live tick's refresh.
 * Measured on an item peek over the e2e board, two writes produced seven
 * refreshes (fourteen item and board RSC fetches). The Next router does not
 * queue them: each new refresh replaces the tree the previous one was
 * fetching, so the writes' tree committed only after the last of them, 3 to
 * 4.6s after the write (docs/archive/TECH_DEBT_RESOLVED.md, "A write's refresh
 * can land after a spec's 5s window on a full board"). So:
 *
 * - **A write is marked, not followed by a refresh.** A component runs its
 *   action inside {@link useWriteTransition} and calls no refresh after it. The
 *   mark holds from the dispatch until the transition's tree has committed,
 *   which is when the action's navigation has landed.
 * - **Every other refresh is a REQUEST** ({@link requestRefresh}): the live
 *   tick, the record bridge, a replayed offline queue, an act that revalidates
 *   nothing. While a write or a refresh is in flight a request waits, and all
 *   waiting requests are ONE pending refresh, started once when the in-flight
 *   one lands. A request may say how to tell that the render which landed in
 *   the meantime already answered it (`stillNeeded`); such a request is then
 *   dropped instead of refreshing the page again.
 *
 * **The holds**, the reasons this gate was first built. Two gestures cannot
 * survive a refresh landing inside them, and both were found in a production
 * build, not in theory:
 *
 * 1. **A board drag.** A drop appends events, which ticks the socket, so a
 *    refresh lands *right as the drop is settling*. That refresh re-renders the
 *    board mid-measure and dnd-kit recurses its measuring layout effects until
 *    React throws "Maximum update depth exceeded". The board wraps a drag in
 *    `beginDnd()`/`endDnd()` (ref-counted, held across the async commit).
 * 2. **A click-initiated navigation** (2026-09-21). A refresh dispatched while a
 *    Link's navigation is still fetching is answered with an empty patch, and
 *    the App Router's tolerance for that is a DOCUMENT navigation
 *    (`completeHardNavigation`, next/dist/client/components/segment-cache).
 *    On the way into an item that turns the intercepted peek into a full page
 *    load, because `@modal` + `(.)items/[id]` intercepts a SOFT navigation
 *    only. The window is real for a person, not only for a spec: create a task,
 *    click the new row, and the tick from your own write is ~300ms behind you.
 *    `holdThroughNavigation()` - armed on any in-app link click by
 *    `RefreshHost` - closes it.
 *
 * A held request waits and then runs once: live updates are delayed a beat,
 * never dropped.
 *
 * Module-level (not React state) on purpose: it must be readable synchronously
 * by components in different trees (the board, the peek, the shell), with no
 * re-render coupling.
 */

/**
 * How long a navigation hold survives on its own. The route change it was armed
 * for releases it (`releaseNavigationHold`, called on every pathname change);
 * this fuse covers the clicks that never navigate - a handler that prevented the
 * default, a link to the URL already open - so one of them cannot park the live
 * refresh indefinitely.
 */
const NAV_HOLD_FUSE_MS = 1500;

/**
 * How long a write mark or an in-flight refresh blocks other refreshes on its
 * own. Each is released by the commit of the tree it fetches; the fuse covers a
 * transition that never commits, so it cannot park every later refresh.
 */
export const IN_FLIGHT_FUSE_MS = 10_000;

/** A request to refresh the current route. */
export interface RefreshRequest {
  /** Who asked, for the reader of a trace. */
  reason: string;
  /**
   * Asked when the waiting refresh is about to start: false means a render that
   * landed while the request waited already answered it. Absent means the
   * request always needs a refresh.
   */
  stillNeeded?: () => boolean;
}

/** What the coordinator needs from its surroundings, injected so its logic is
 *  testable without a browser, a router or React. */
export interface RefreshEnvironment {
  /** Start a refresh. Answers false when nothing can run one yet (no router
   *  registered), and the requests keep waiting. */
  run: (reasons: string[]) => boolean;
  /** Run `fn` after the current task. A landing is reported from inside
   *  React's effect flush, and the render that landed re-declares its live
   *  surfaces in the same flush; deciding after it lets a request's
   *  `stillNeeded` see those declarations. */
  defer: (fn: () => void) => void;
  /** A timer for the fuses; returns its cancel. */
  setTimer: (fn: () => void, ms: number) => () => void;
}

/**
 * The coordinator's logic: at most one pending refresh, gated on the holds, on
 * an in-flight refresh and on in-flight writes.
 */
export class RefreshCoordinator {
  private dragDepth = 0;
  private navHeld = false;
  private navFuse: (() => void) | null = null;
  private refreshing = false;
  private refreshFuse: (() => void) | null = null;
  private writes = 0;
  /** The ONE pending refresh, as the requests waiting on it. */
  private pending: RefreshRequest[] = [];

  constructor(private readonly env: RefreshEnvironment) {}

  /** Nothing may start now: a gesture is held, or a write or a refresh is in flight. */
  blocked(): boolean {
    return this.dragDepth > 0 || this.navHeld || this.refreshing || this.writes > 0;
  }

  /** Whether a refresh is waiting. */
  hasPending(): boolean {
    return this.pending.length > 0;
  }

  /** Ask for a refresh: it starts now when nothing blocks it, else it joins the pending one. */
  request(req: RefreshRequest): void {
    this.pending.push(req);
    this.pump();
  }

  /** Start the pending refresh when nothing blocks it and one of its requests still needs it. */
  pump(): void {
    if (this.blocked() || this.pending.length === 0) return;
    // Every request is asked, at start time, never at request time.
    const due = this.pending.filter((r) => r.stillNeeded?.() ?? true);
    if (due.length === 0) {
      this.pending = [];
      return;
    }
    if (!this.env.run(due.map((r) => r.reason))) return;
    this.pending = [];
    this.refreshing = true;
    this.refreshFuse = this.env.setTimer(() => this.landed(), IN_FLIGHT_FUSE_MS);
  }

  /** The refresh this coordinator started has committed (or its fuse burned). */
  landed(): void {
    if (!this.refreshing) return;
    this.refreshing = false;
    this.refreshFuse?.();
    this.refreshFuse = null;
    this.env.defer(() => this.pump());
  }

  /**
   * A write's action is on its way, and its navigation is the refresh, so no
   * other refresh starts until it lands. Returns the release (idempotent),
   * called once the write's tree has committed, or by the fuse.
   */
  beginWrite(): () => void {
    this.writes += 1;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      cancelFuse();
      this.writes = Math.max(0, this.writes - 1);
      this.env.defer(() => this.pump());
    };
    const cancelFuse = this.env.setTimer(release, IN_FLIGHT_FUSE_MS);
    return release;
  }

  /** Enter a drag (or hold across the drop's async commit). Ref-counted. */
  beginDnd(): void {
    this.dragDepth += 1;
  }

  /** Leave a drag / release a hold. When the last one clears, start a waiting refresh. */
  endDnd(): void {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.pump();
  }

  /**
   * Hold from an in-app link click until the route it asked for has landed.
   * ONE slot, not a ref count: a second click re-arms the same hold rather than
   * stacking holds that a single route change cannot clear.
   */
  holdThroughNavigation(): void {
    this.navHeld = true;
    this.navFuse?.();
    this.navFuse = this.env.setTimer(() => this.releaseNavigationHold(), NAV_HOLD_FUSE_MS);
  }

  /** The navigation landed (or the fuse burned through): open the gate and start. */
  releaseNavigationHold(): void {
    this.navFuse?.();
    this.navFuse = null;
    if (!this.navHeld) return;
    this.navHeld = false;
    this.pump();
  }
}

// --- the page's coordinator --------------------------------------------------

let runner: ((reasons: string[]) => void) | null = null;

const coordinator = new RefreshCoordinator({
  run: (reasons) => {
    if (!runner) return false;
    runner(reasons);
    return true;
  },
  defer: (fn) => {
    setTimeout(fn, 0);
  },
  setTimer: (fn, ms) => {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
  },
});

/**
 * Register what starts a router refresh (`RefreshHost`, mounted once in the
 * root layout). It must call {@link refreshLanded} once the refreshed tree has
 * committed. Returns the unregister.
 */
export function registerRefreshRunner(run: (reasons: string[]) => void): () => void {
  runner = run;
  coordinator.pump();
  return () => {
    if (runner === run) runner = null;
  };
}

/** The refresh the runner started has committed. */
export function refreshLanded(): void {
  coordinator.landed();
}

/** Ask for a refresh of the current route (see the module comment). */
export function requestRefresh(reason: string, opts?: { stillNeeded?: () => boolean }): void {
  coordinator.request(opts?.stillNeeded ? { reason, stillNeeded: opts.stillNeeded } : { reason });
}

/** Enter a drag (or hold across the drop's async commit). Ref-counted. */
export function beginDnd(): void {
  coordinator.beginDnd();
}

/** Leave a drag / release a hold. When the last one clears, start a waiting refresh. */
export function endDnd(): void {
  coordinator.endDnd();
}

/**
 * Hold from the PRESS, not only from activation. dnd-kit activates a pointer
 * drag after 4px of travel, and `beginDnd` above runs at activation - so a
 * refresh arriving between the press and that activation (the stream's hello
 * fires one ~300ms after every page mount) used to re-render the pressed node
 * mid-gesture. Call from the activator's `onPointerDown`; the hold releases
 * itself on the next pointerup / pointercancel anywhere. A drag that does
 * activate takes its own `beginDnd` and holds through the commit, so the ref
 * count balances either way.
 */
export function holdThroughPress(): void {
  beginDnd();
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    window.removeEventListener('pointerup', release, true);
    window.removeEventListener('pointercancel', release, true);
    endDnd();
  };
  window.addEventListener('pointerup', release, true);
  window.addEventListener('pointercancel', release, true);
}

/** Hold the refresh from an in-app link click until the route it asked for has landed. */
export function holdThroughNavigation(): void {
  coordinator.holdThroughNavigation();
}

/** The navigation landed (or the fuse burned through): open the gate and start. */
export function releaseNavigationHold(): void {
  coordinator.releaseNavigationHold();
}

/**
 * `useTransition` for a write. Run the server action inside it and call no
 * refresh afterwards: the action revalidates, so its response is the refresh.
 * `pending` stays true until the action's navigation has committed (React
 * entangles the router's update with the async action), and for that long the
 * coordinator starts no other refresh - a live tick from the write's own event
 * would otherwise restart the tree the write is waiting for.
 */
export function useWriteTransition(): [boolean, TransitionStartFunction] {
  const [pending, start] = useTransition();
  const release = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (pending || !release.current) return;
    release.current();
    release.current = null;
  }, [pending]);

  useEffect(
    () => () => {
      release.current?.();
      release.current = null;
    },
    [],
  );

  const startWrite = useCallback<TransitionStartFunction>(
    (callback) => {
      if (!release.current) release.current = coordinator.beginWrite();
      start(callback);
    },
    [start],
  );

  return [pending, startWrite];
}
